import bendPoints from "../data/bend-points.json";
import type { PersonInputs } from "./types";

/**
 * Compute Primary Insurance Amount (PIA) from Average Indexed Monthly Earnings (AIME)
 * using SSA's tiered bend-point formula. Result is rounded down to nearest dime per SSA rules.
 *
 * Formula:
 *   90% of AIME at or below first bend point
 * + 32% of AIME between first and second bend points
 * + 15% of AIME above second bend point
 */
export function computePiaFromAime(aime: number): number {
  if (aime <= 0) return 0;
  const { first_bend_point_aime: b1, second_bend_point_aime: b2, tier_pcts } = bendPoints;
  const tier1 = Math.min(aime, b1) * tier_pcts.below_first;
  const tier2 = Math.max(0, Math.min(aime, b2) - b1) * tier_pcts.between;
  const tier3 = Math.max(0, aime - b2) * tier_pcts.above_second;
  const raw = tier1 + tier2 + tier3;
  return Math.floor(raw * 10) / 10;
}

/**
 * Resolve a PersonInputs to a numeric PIA. Prefers explicit pia; falls back to AIME conversion.
 * Throws if neither is supplied or both are non-positive.
 */
export function resolvePia(p: PersonInputs): number {
  if (typeof p.pia === "number" && p.pia > 0) return p.pia;
  if (typeof p.aime === "number" && p.aime > 0) return computePiaFromAime(p.aime);
  throw new Error("PersonInputs must provide a positive `pia` or a positive `aime`.");
}

/** Bend-points data-file last_verified date for PDF/meta footers. */
export function getBendPointsVerified(): string {
  return bendPoints.last_verified;
}
